import { ts } from 'src/utils/translate';
import { BTNS } from 'src/utils/constants';

export const SECTION_BUTTON_FLOOR_PREFIX = 'floor_';
export const SECTION_BUTTON_FLOOR_ROOMS_PREFIX = 'floorRooms_';
export const SECTION_BUTTON_ROOM_PREFIX = 'room_';

export interface SectionButtonDescriptor {
  buttonId: string,
  propertyKey: string,
  /**
   * is enabled/disabled (grayed out)
   */
  enabled: boolean,
  /**
   * unused, use customizable instead
   */
  visible: boolean,
  /**
   * is displayed or not
   */
  customizable?: boolean,
  /**
   * is the eye displayed
   */
  toggleable?: boolean,
  type: 'normal' | 'floor' | 'floor_rooms' | 'room',
  state: number | string
  section: 'customizations' | 'sections',
  icon?: string,
  iconExtra?: string,
  label?: string,
  values?: any[], // used for dropdowns
  disableReason?: string,
  newFeature?: boolean,
  testFeature?: boolean,
  /**
   * only for type: floor, floor_rooms, room
   */
  floorId?: number,
  /**
   * only for type: floor, floor_rooms, room
   */
  roomId?: number,
  contentCssSelector?: string,
  dependencies?: string[]
}

export function initButtonDescriptors(): SectionButtonDescriptor[] {
  return [
    {
      buttonId: BTNS.CLIENT_TYPE,
      propertyKey: 'include_client_type',
      enabled: true,
      visible: true,
      customizable: false,
      toggleable: true,
      type: 'normal',
      state: 'residential_client',
      section: 'customizations',
      icon: '/images/offer_icons/Client_type.svg',
      values: [
        {
          value: 'residential_client',
          text: ts('residental_client', 'Residental client'),
        },
        {
          value: 'commercial_client',
          text: ts('commercial_client', 'Commercial client'),
        },
        {value: 'electrician', text: ts('electrician', 'Electrician')},
      ],
    },
    {
      buttonId: BTNS.HEADER,
      propertyKey: 'include_header',
      enabled: true,
      visible: true,
      customizable: true,
      type: 'normal',
      state: 1,
      section: 'customizations',
      icon: '/images/offer_icons/header.svg',
      label: ts('header', 'Header'),
    },
    {
      buttonId: BTNS.FOOTER,
      propertyKey: 'include_footer',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'customizations',
      icon: '/images/offer_icons/footer.svg',
      label: ts('footer', 'Footer'),
    },
    {
      buttonId: BTNS.T_DETAILS,
      propertyKey: 'include_item_details',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'customizations',
      icon: "/images/offer_icons/item_details.svg",
      label: ts("items_details", "Item details"),
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.T_ARTICLENUMBERS,
      propertyKey: 'include_article_number',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'customizations',
      icon: "/images/offer_icons/article_number.svg",
      label: ts("articles_serial_number", "Article numbers"),
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.CURRENCY_VISIBILITY,
      propertyKey: 'include_currency_visibility',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 2,
      section: 'customizations',
      icon: "/images/offer_icons/prices.svg",
      values: [
        { value: 2, text: ts("q_ppi_all", "Include item and total prices") },
        {
          value: 1,
          text: ts("q_ppi_total", "Only include total price"),
          // valueExtra: "fa fa-sum",
        },
        { value: 0, text: ts("q_ppi_hide", "Hide all prices") },
      ],
    },
    {
      buttonId: BTNS.SELECTED_CURRENCY,
      propertyKey: "selected_currency",
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: "eur",
      section: 'customizations',
      icon: "/images/offer_icons/valuta.svg",
      values: [
        { value: "eur", text: "EUR", icon: "fa fa-eur" },
        { value: "huf", text: "HUF", icon: "fa fa-eur" },
        { value: "gbp", text: "GBP", icon: "fa fa-eur" },
        { value: "czk", text: "CZK", icon: "fa fa-eur" },
        { value: "usd", text: "USD", icon: "fa fa-eur" },
        { value: "pln", text: "PLN", icon: "fa fa-eur" },
        { value: "chf", text: "CHF", icon: "fa fa-eur" },
      ],
    },
    {
      buttonId: BTNS.T_COMMENTS,
      propertyKey: 'include_comments',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'customizations',
      icon: "/images/offer_icons/comment.svg",
      label: ts("comments", "Comments"),
      dependencies: [BTNS.T_DETAILS],
    },
    {
      buttonId: BTNS.MAP_NOTES,
      propertyKey: 'include_notes',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'customizations',
      icon: "/images/offer_icons/notes.svg",
      label: ts("notes", "Notes"),
      dependencies: [BTNS.FLOORMAPS, BTNS.ROOMMAPS],
    },
    {
      buttonId: BTNS.MAP_SYMBOLS,
      propertyKey: 'include_map_symbols',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'customizations',
      icon: "/images/offer_icons/symbols.svg",
      label: ts("symbols", "Symbols"),
      dependencies: [BTNS.FLOORMAPS, BTNS.ROOMMAPS],
    },
    {
      buttonId: BTNS.COVER_PAGE,
      propertyKey: 'include_cover_page',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/cover_page.svg",
      label: ts("cover_page", "Cover Page"),
      contentCssSelector: '.cover-page'
    },
    {
      buttonId: BTNS.GENERAL,
      propertyKey: 'include_general_information',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/general_information.svg",
      label: ts("general_information", "General information"),
      contentCssSelector: '.general-info'
    },
    {
      buttonId: BTNS.FUNCTION_DESCRIPTION,
      propertyKey: 'include_function_description',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: '/images/offer_icons/function_description.svg',
      label: ts("function_description", "Function description"),
      contentCssSelector: '.function-description'
    },
    {
      buttonId: BTNS.TABLES,
      propertyKey: 'include_tables',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/tables.svg",
      label: ts('tables', 'Tables'),
      contentCssSelector: '.dataTable'
    },
    {
      buttonId: BTNS.T_LOXONE_DIN_DEVICES,
      propertyKey: 'include_functions_table',
      enabled: true,
      visible: false,
      customizable: false,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: '/images/offer_icons/Loxone_din_devices.svg',
      label: ts("din_device", "DIN Devices"),
      contentCssSelector: '[data-entitytype="functiontable"]',
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.T_NHC_DIN_DEVICES,
      propertyKey: 'include_nhc_din_table',
      enabled: true,
      visible: false,
      customizable: false,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: '/images/offer_icons/NHC_din_devices.svg',
      label: ts("din_device", "DIN Devices"),
      contentCssSelector: '#nhcdintable',
       dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.T_CAB_CONV,
      propertyKey: 'include_conv_dev_in_ec',
      enabled: true,
      visible: true,
      customizable: false,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/conventional_devices.svg",
      label: ts("quote_eoutlet_conv", "Conventional devices in the electrical cabinet"),
      contentCssSelector: '[data-entitytype="convfunctiontable"]',
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.T_RACK,
      propertyKey: 'include_it_products',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/IT.svg",
      label: ts("it_products", "IT Products"),
      contentCssSelector: '#racktable',
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.T_CABLES,
      propertyKey: 'include_cables',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/cable.svg",
      label: ts("q_cables", "Cables"),
      contentCssSelector: '#projectcablestable',
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.T_BACK_BOXES,
      propertyKey: 'include_back_boxes',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/back_boxes.svg",
      label: ts("back_boxes", "Back boxes"),
      contentCssSelector: '.back-box',
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.CUSTOM_SECTION,
      propertyKey: 'include_custom_section',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/custom_section.svg",
      label: ts("custom_section", "Custom section"),
      contentCssSelector: '#new-sections',
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.PINOUT,
      label: ts("q_pinout", "Pinout"),
      propertyKey: 'include_pinout',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/Pinout.svg",
      contentCssSelector: '.pinout-header'
    },
    {
      buttonId: BTNS.CABLE_LABELS,
      propertyKey: 'include_cable_labels',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/printable_symbols.svg",
      label: ts("q_printable_symbols", "Printable Symbols"),
      contentCssSelector: '.quote-cable-labels-container'
    },
    {
      buttonId: BTNS.MAP_WIRING,
      propertyKey: 'include_wiring',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/wiring.svg",
      label: ts("q_wiring", "Wiring"),
      contentCssSelector: '[data-wiringfloorid]',
      dependencies: [BTNS.FLOORMAPS],
    },
    {
      buttonId: BTNS.MAP_WIFI,
      propertyKey: 'include_wifi_coverage',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/wifi_coverage.svg",
      label: ts("wifi_coverage", "Wifi Coverage"),
      contentCssSelector: '[data-wififloorid]',
      dependencies: [BTNS.FLOORMAPS],
    },
    {
      buttonId: BTNS.MAP_CAMERA,
      propertyKey: 'include_camera_coverage',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/camera_coverage.svg",
      label: ts("camera_coverage", "Camera Coverage"),
      contentCssSelector: '[data-camerafloorid]',
      dependencies: [BTNS.FLOORMAPS],
    },
    {
      buttonId: BTNS.MAP_LIGHTING,
      propertyKey: 'include_lighting',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 0,
      section: 'sections',
      icon: "/images/offer_icons/lighting_calculation.svg",
      label: ts("docu_lightcalc_heading", "Lighting Calculation"),
      dependencies: [BTNS.FLOORMAPS],
      newFeature: true,
      testFeature: true,
      contentCssSelector: '.lighting-calculation'
    },
    {
      buttonId: BTNS.BOM,
      propertyKey: 'include_bom',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/BOM.svg",
      label: ts("bom", "Bill of Materials"),
      contentCssSelector: '#react-quote-bom'
    },
    {
      buttonId: BTNS.LABOUR_COST,
      propertyKey: 'include_labour_cost',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/labour_cost.svg",
      label: ts("labour_cost", "Labour Cost"),
      contentCssSelector: '#projectcustomtable'
    },
    {
      buttonId: BTNS.DISCOUNTS,
      propertyKey: 'include_discount',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/discount.svg",
      label: ts("quote_discounts", "Discounts"),
      contentCssSelector: '.quote-footer',
      dependencies: [BTNS.TABLES],
    },
    {
      buttonId: BTNS.TOTAL_PRICE,
      propertyKey: 'include_total_price',
      enabled: true,
      visible: true,
      customizable: true,
      toggleable: true,
      type: 'normal',
      state: 1,
      section: 'sections',
      icon: "/images/offer_icons/total_price.svg",
      label: ts("total_price", "Total Price"),
      contentCssSelector: '.total-price',
      dependencies: [BTNS.TABLES],
    },
  ];
}
